import { EXTERNAL_LINKS } from '@/constants/links';

/**
 * Detect if the user is on macOS (not iOS).
 * Uses navigator.userAgentData (modern Chromium) with navigator.platform fallback.
 * Mac platforms start with "Mac" (e.g., "MacIntel", "MacPPC").
 * iOS platforms are "iPhone", "iPad", "iPod" — excluded.
 */
export const isMacOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  if ('userAgentData' in navigator) {
    const uaData = (navigator as any).userAgentData;
    if (uaData?.platform) {
      return uaData.platform === 'macOS';
    }
  }

  return /^Mac/.test(navigator.platform);
};

/**
 * Detect if the user is on Windows.
 */
export const isWindows = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  if ('userAgentData' in navigator) {
    const uaData = (navigator as any).userAgentData;
    if (uaData?.platform) {
      return uaData.platform === 'Windows';
    }
  }

  return /Win/.test(navigator.platform);
};

/**
 * Returns the correct DMG download URL based on CPU architecture.
 *
 * Detection strategy:
 * - Chromium browsers expose architecture via navigator.userAgentData (async API).
 *   We use a sync cache that's populated on module load.
 * - Safari/Firefox don't expose arch. We default to x64 (Intel) because:
 *   - x64 runs on Apple Silicon via Rosetta 2 (works fine, slight perf overhead)
 *   - ARM64 does NOT run on Intel Macs (hard failure, "not supported" error)
 *   - So x64 is the safe universal fallback.
 */
let detectedArch: 'arm64' | 'x64' = 'x64'; // safe default

// Eagerly detect architecture on module load (async, updates cache)
if (typeof navigator !== 'undefined' && 'userAgentData' in navigator) {
  const uaData = (navigator as any).userAgentData;
  if (uaData?.getHighEntropyValues) {
    uaData.getHighEntropyValues(['architecture']).then((values: any) => {
      if (values.architecture === 'arm') {
        detectedArch = 'arm64';
      }
    }).catch(() => {
      // Permission denied or unsupported — keep x64 default
    });
  }
}

/**
 * Returns the direct DMG download URL for the user's Mac architecture.
 * ARM64 (Apple Silicon) users get the native build.
 * Intel and unknown-arch users get x64 (safe fallback via Rosetta 2).
 */
export const getDownloadUrl = (): string => {
  return detectedArch === 'arm64'
    ? EXTERNAL_LINKS.DESKTOP_DOWNLOAD_DMG
    : EXTERNAL_LINKS.DESKTOP_DOWNLOAD_DMG_X64;
};
